import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ChevronLeft, Plus, Trash2, Pencil, MapPin, X, RefreshCw } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import StatusBadge from "@/components/StatusBadge";
import CostHealthBar from "@/components/CostHealthBar";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import SearchableSelect from "@/components/SearchableSelect";

const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
const emptyCO = { code: "", name: "", amount: "", status: "pending" };
const emptyAddressForm = { label: "", address_line1: "", city: "", state: "", zip: "", site_contact_name: "", site_manager: "" };

export default function JobDetail() {
  const { id } = useParams();
  const [job, setJob] = useState(null);
  const [changeOrders, setChangeOrders] = useState([]);
  const [costCodes, setCostCodes] = useState([]);
  const [costs, setCosts] = useState(null);
  const [form, setForm] = useState(null);
  const [budgetTotal, setBudgetTotal] = useState("");
  const [taxRate, setTaxRate] = useState("");
  const [budgetLines, setBudgetLines] = useState([]);
  const [showCO, setShowCO] = useState(false);
  const [editingCO, setEditingCO] = useState(null);
  const [coForm, setCoForm] = useState(emptyCO);
  const [allAddresses, setAllAddresses] = useState([]);
  // QuickBooks jobs: only their QuickBooks shipping address or the company
  // main (default) address can be attached (Test Drive 2026-10-21).
  const [addressOptions, setAddressOptions] = useState({ addresses: [], restricted: false });
  const [attachId, setAttachId] = useState("");
  const [showNewAddress, setShowNewAddress] = useState(false);
  const [addressForm, setAddressForm] = useState(emptyAddressForm);
  const navigate = useNavigate();

  const loadCosts = () => api.get(`/contractor/jobs/${id}/costs`).then((r) => setCosts(r.data));

  const load = () => {
    api.get(`/contractor/jobs/${id}`).then((r) => {
      setJob(r.data);
      setForm({ code: r.data.code, name: r.data.name, address: r.data.address || "", customer_name: r.data.customer_name || "", start_date: r.data.start_date || "" });
      setBudgetTotal(r.data.budget_total ?? "");
      setTaxRate(r.data.tax_rate ?? "");
      setBudgetLines(r.data.budget_lines || []);
    });
    api.get(`/contractor/jobs/${id}/change-orders`).then((r) => setChangeOrders(r.data));
    api.get(`/contractor/jobs/${id}/address-options`).then((r) => setAddressOptions(r.data));
    loadCosts();
  };
  useEffect(() => { load(); }, [id]);
  useEffect(() => {
    api.get("/contractor/cost-codes").then((r) => setCostCodes(r.data));
    api.get("/contractor/delivery-addresses").then((r) => setAllAddresses(r.data));
  }, []);

  // Package 23 — job-profile addresses.
  // QuickBooks jobs: exactly two addresses — the QuickBooks shipping
  // address and the company main (default) address.
  const shipAddress = addressOptions.addresses.find((a) => a.source === "quickbooks");
  const mainAddress = addressOptions.addresses.find((a) => a.is_default);
  const [pullingShip, setPullingShip] = useState(false);
  const [choosingAddress, setChoosingAddress] = useState(false);
  // One delivery address per QuickBooks job — either one, switchable.
  const chooseAddress = async (addressId) => {
    setChoosingAddress(true);
    try {
      await api.patch(`/contractor/jobs/${id}`, { delivery_address_ids: [addressId] });
      toast.success("Delivery address updated");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't change the delivery address");
    } finally {
      setChoosingAddress(false);
    }
  };
  const pullShippingAddress = async () => {
    setPullingShip(true);
    try {
      await api.post(`/contractor/jobs/${id}/pull-shipping-address`);
      toast.success("Shipping address pulled from QuickBooks");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't pull the shipping address");
    } finally {
      setPullingShip(false);
    }
  };
  const attachedIds = job?.delivery_address_ids || [];
  const jobAddresses = (addressOptions.restricted ? addressOptions.addresses : allAddresses).filter((a) => attachedIds.includes(a.id));
  const unattachedAddresses = addressOptions.addresses.filter((a) => !attachedIds.includes(a.id));

  const attachAddress = async () => {
    if (!attachId) return;
    try {
      await api.patch(`/contractor/jobs/${id}`, { delivery_address_ids: [...(job.delivery_address_ids || []), attachId] });
      setAttachId("");
      toast.success("Address attached to job");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to attach address");
    }
  };

  const detachAddress = async (addressId) => {
    try {
      await api.patch(`/contractor/jobs/${id}`, { delivery_address_ids: (job.delivery_address_ids || []).filter((a) => a !== addressId) });
      toast.success("Address removed from job");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove address");
    }
  };

  const createJobAddress = async () => {
    if (!addressForm.label || !addressForm.address_line1) return toast.error("Label and address line 1 required");
    try {
      await api.post("/contractor/delivery-addresses", { ...addressForm, job_id: id });
      toast.success("Job-site address added");
      setShowNewAddress(false);
      setAddressForm(emptyAddressForm);
      api.get("/contractor/delivery-addresses").then((r) => setAllAddresses(r.data));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create address");
    }
  };

  const costCodeLabel = (ccId) => {
    const cc = costCodes.find((c) => c.id === ccId);
    return cc ? `${cc.code} — ${cc.name}` : ccId;
  };

  if (!job || !form) return <div className="p-8 text-slate-400 text-sm">Loading…</div>;

  // Package 46 — saved on its own so it works on QuickBooks-governed jobs
  // too (name/code are QuickBooks'; the tax rate is ScapeSource's).
  const saveTaxRate = async () => {
    try {
      await api.patch(`/contractor/jobs/${id}`, { tax_rate: taxRate === "" ? null : Number(taxRate) });
      toast.success("Tax rate saved");
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(Array.isArray(detail) ? detail.map((d) => d.msg).join(" ") : detail || "Failed to save tax rate");
    }
  };

  const saveDetails = async () => {
    try {
      await api.patch(`/contractor/jobs/${id}`, form);
      toast.success("Job updated");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update job");
    }
  };

  const toggleArchive = async () => {
    const nextStatus = job.status === "closed" ? "active" : "closed";
    try {
      await api.patch(`/contractor/jobs/${id}`, { status: nextStatus });
      toast.success(nextStatus === "closed" ? "Job archived" : "Job reopened");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update status");
    }
  };

  const addBudgetLine = () => setBudgetLines([...budgetLines, { cost_code_id: costCodes[0]?.id || "", amount: 0 }]);
  const updateBudgetLine = (i, patch) => setBudgetLines(budgetLines.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const removeBudgetLine = (i) => setBudgetLines(budgetLines.filter((_, idx) => idx !== i));

  // The budget total is the sum of its lines whenever there are any.
  const linesTotal = budgetLines.reduce((s, l) => s + (Number(l.amount) || 0), 0);

  const saveBudget = async () => {
    try {
      await api.patch(`/contractor/jobs/${id}`, {
        budget_total: budgetLines.length ? linesTotal : budgetTotal === "" ? null : Number(budgetTotal),
        budget_lines: budgetLines.map((l) => ({ cost_code_id: l.cost_code_id, amount: Number(l.amount) })),
      });
      toast.success("Budget updated");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update budget");
    }
  };

  const openNewCO = () => { setEditingCO(null); setCoForm(emptyCO); setShowCO(true); };
  const openEditCO = (co) => { setEditingCO(co); setCoForm({ code: co.code, name: co.name, amount: co.amount ?? "", status: co.status }); setShowCO(true); };

  const saveCO = async () => {
    if (!coForm.code || !coForm.name) return toast.error("Code and name required");
    const body = { ...coForm, amount: coForm.amount === "" ? null : Number(coForm.amount) };
    try {
      if (editingCO) {
        await api.patch(`/contractor/jobs/${id}/change-orders/${editingCO.id}`, body);
        toast.success("Change order updated");
      } else {
        await api.post(`/contractor/jobs/${id}/change-orders`, body);
        toast.success("Change order created");
      }
      setShowCO(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save change order");
    }
  };

  const removeCO = async (co) => {
    await api.delete(`/contractor/jobs/${id}/change-orders/${co.id}`);
    toast.success("Change order deleted");
    load();
  };

  return (
    <div className="p-6 md:p-8 max-w-3xl" data-testid="job-detail-page">
      <Link to="/contractor/jobs" className="flex items-center gap-1 text-[12.5px] text-slate-500 hover:text-slate-700">
        <ChevronLeft size={14} /> Back to Jobs
      </Link>

      <div className="flex items-center justify-between flex-wrap gap-4 mt-3">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-extrabold tracking-tight">{job.code}</h1>
          <StatusBadge status={job.status} />
        </div>
        <button onClick={toggleArchive} data-testid="archive-job-button" className="px-4 py-2 rounded-lg border border-slate-200 text-[13px] font-semibold hover:bg-slate-50">
          {job.status === "closed" ? "Reopen Job" : "Archive Job"}
        </button>
      </div>

      {costs && (
        <div className="border border-slate-200 rounded-xl p-5 mt-5" data-testid="job-costs-card">
          <h2 className="font-semibold text-[14px]">Costs</h2>
          <div className="mt-3 grid grid-cols-3 gap-3 text-[13px]">
            <div>
              <div className="text-slate-400 text-[12px]">Committed</div>
              <div className="font-bold mono" data-testid="job-costs-committed">{fmt(costs.committed)}</div>
            </div>
            <div>
              <div className="text-slate-400 text-[12px]">Actual</div>
              <div className="font-bold mono" data-testid="job-costs-actual">{fmt(costs.actual)}</div>
            </div>
            <div>
              <div className="text-slate-400 text-[12px]">Pending</div>
              <div className="font-bold mono" data-testid="job-costs-pending">{fmt(costs.pending)}</div>
            </div>
          </div>

          {costs.budget_total != null ? (
            <div className="mt-4">
              <div className="flex justify-between text-[12.5px] text-slate-500 mb-1">
                <span>Budget {fmt(costs.budget_total)}</span>
                <span data-testid="job-costs-remaining">
                  {costs.remaining >= 0 ? `${fmt(costs.remaining)} remaining` : `${fmt(Math.abs(costs.remaining))} over`}
                </span>
              </div>
              <CostHealthBar spent={costs.spent} budgetTotal={costs.budget_total} health={costs.health} testId="job-costs-health-bar" />
              {costs.on_hold > 0 && (
                <div className="mt-3 text-[12.5px] text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2" data-testid="job-costs-on-hold">
                  {fmt(costs.on_hold)} in vendor invoices is waiting on budget. Raise the budget below to release it — it isn't counted until then.
                </div>
              )}

              {costs.budget_by_cost_code && costs.budget_by_cost_code.length > 0 && (
                <div className="mt-4 overflow-x-auto">
                  <table className="w-full text-[12.5px]" data-testid="job-costs-by-code-table">
                    <thead>
                      <tr className="text-left text-slate-400">
                        <th className="pb-1.5 font-medium">Cost Code</th>
                        <th className="pb-1.5 font-medium">Budget</th>
                        <th className="pb-1.5 font-medium">Committed</th>
                        <th className="pb-1.5 font-medium">Actual</th>
                        <th className="pb-1.5 font-medium">Remaining</th>
                        <th className="pb-1.5 font-medium">Health</th>
                      </tr>
                    </thead>
                    <tbody>
                      {costs.budget_by_cost_code.map((l) => (
                        <tr key={l.cost_code_id} className="border-t border-slate-100" data-testid={`cost-code-row-${l.cost_code_id}`}>
                          <td className="py-1.5">{costCodeLabel(l.cost_code_id)}</td>
                          <td className="py-1.5 mono">{fmt(l.budget_amount)}</td>
                          <td className="py-1.5 mono">{fmt(l.committed)}</td>
                          <td className="py-1.5 mono">{fmt(l.actual)}</td>
                          <td className="py-1.5 mono">{fmt(l.remaining)}</td>
                          <td className="py-1.5"><StatusBadge status={l.health} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : (
            <div className="mt-3 text-[11.5px] text-slate-400">No budget set for this job — showing committed/actual/pending only.</div>
          )}
        </div>
      )}

      <div className="border border-slate-200 rounded-xl p-5 mt-5 space-y-3">
        <h2 className="font-semibold text-[14px]">Details</h2>
        <input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="Code" data-testid="job-detail-code-input" className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" data-testid="job-detail-name-input" className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
        <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Address" data-testid="job-detail-address-input" className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
        <input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} placeholder="Customer name" data-testid="job-detail-customer-input" className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
        <div>
          <label className="text-[12.5px] text-slate-500">Start date</label>
          <input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="job-detail-start-date-input" className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
        </div>
        <button onClick={saveDetails} data-testid="save-job-details-button" className="px-4 py-2 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-[13px] font-semibold">Save Changes</button>
      </div>

      <div className="border border-slate-200 rounded-xl p-5 mt-5" data-testid="job-tax-rate-card">
        <h2 className="font-semibold text-[14px]">Sales Tax</h2>
        <p className="text-[12px] text-slate-400 mt-1">The tax rate for this job's location. Expense Log entries marked Taxable add tax at this rate.</p>
        <div className="mt-3 flex items-center gap-2">
          <div className="relative w-32">
            <input type="number" min="0" max="25" step="0.001" value={taxRate} onChange={(e) => setTaxRate(e.target.value)} placeholder="8.25" data-testid="job-tax-rate-input" className="w-full h-10 pl-3 pr-7 rounded-lg border border-slate-200 text-sm" />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">%</span>
          </div>
          <button onClick={saveTaxRate} data-testid="save-job-tax-rate-button" className="px-4 h-10 rounded-lg border border-slate-200 text-[13px] font-semibold hover:bg-slate-50">Save</button>
        </div>
      </div>

      {addressOptions.restricted ? (
        <div className="border border-slate-200 rounded-xl p-5 mt-5" data-testid="qbo-job-addresses">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h2 className="font-semibold text-[14px]">Delivery Addresses</h2>
            <button onClick={pullShippingAddress} disabled={pullingShip} data-testid="pull-shipping-address-button" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-[12.5px] font-semibold hover:bg-slate-50 disabled:opacity-60">
              <RefreshCw size={12} className={pullingShip ? "animate-spin" : ""} /> {pullingShip ? "Pulling…" : "Pull shipping address from QuickBooks"}
            </button>
          </div>
          <p className="text-[12px] text-slate-400 mt-1">Choose where this job's purchases deliver — the QuickBooks shipping address or the company main address. You can switch any time.</p>
          <div className="mt-3 grid sm:grid-cols-2 gap-3">
            {[
              ["QuickBooks shipping address", shipAddress, "This job has no shipping address yet — pull it from QuickBooks.", "job-ship-address"],
              ["Company main address", mainAddress, "No main address yet — mark your main office as the default under Delivery Addresses.", "job-main-address"],
            ].map(([title, a, empty, testId]) => {
              const selected = !!a && attachedIds.length === 1 && attachedIds[0] === a.id;
              return (
              <label key={testId} data-testid={testId}
                className={`block rounded-lg px-3.5 py-3 border ${a ? "cursor-pointer" : "opacity-70"} ${selected ? "border-sky-500 ring-2 ring-sky-100 bg-sky-50/40" : "border-slate-200 hover:border-slate-300"}`}>
                <div className="flex items-center gap-2">
                  <input type="radio" name="job-delivery-address" checked={selected} disabled={!a || choosingAddress} onChange={() => chooseAddress(a.id)} data-testid={`${testId}-radio`} />
                  <span className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">{title}</span>
                  {selected && <span className="ml-auto text-[10.5px] font-bold text-sky-700">Delivery address</span>}
                </div>
                {a ? (
                  <div className="mt-1.5 text-[13px] flex gap-2">
                    <MapPin size={13} className="text-slate-400 mt-0.5 shrink-0" />
                    <div>
                      <div className="font-medium">{a.label}</div>
                      <div className="text-slate-500 text-[12.5px]">{[a.address_line1, a.address_line2].filter(Boolean).join(", ")}</div>
                      <div className="text-slate-500 text-[12.5px]">{[a.city, a.state, a.zip].filter(Boolean).join(", ")}</div>
                    </div>
                  </div>
                ) : <div className="mt-1.5 text-[12.5px] text-amber-700">{empty}</div>}
              </label>
              );
            })}
          </div>
        </div>
      ) : (
      <div className="border border-slate-200 rounded-xl p-5 mt-5">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-[14px]">Delivery Addresses</h2>
          {!addressOptions.restricted && (
            <button onClick={() => setShowNewAddress(true)} data-testid="add-job-site-address-button" className="flex items-center gap-1.5 text-[12.5px] font-semibold text-sky-600">
              <Plus size={13} /> Add job-site address
            </button>
          )}
        </div>
        <p className="text-[12px] text-slate-400 mt-1">
          {addressOptions.restricted
            ? "Deliveries go to this job's QuickBooks shipping address or your company's main address."
            : "Purchases for this job only see the addresses listed here — if there's just one, it's picked automatically."}
        </p>
        <div className="mt-3 space-y-2" data-testid="job-addresses-list">
          {jobAddresses.map((a) => (
            <div key={a.id} className="flex items-center justify-between border border-slate-100 rounded-lg px-3 py-2" data-testid={`job-address-row-${a.id}`}>
              <div className="flex items-center gap-2 text-[13px]">
                <MapPin size={13} className="text-slate-400" />
                <span className="font-medium">{a.label}</span>
                <span className="text-slate-400 text-[12px]">{[a.city, a.state].filter(Boolean).join(", ")}</span>
              </div>
              <button onClick={() => detachAddress(a.id)} data-testid={`detach-address-${a.id}`} className="text-slate-400 hover:text-red-500"><X size={14} /></button>
            </div>
          ))}
          {jobAddresses.length === 0 && <div className="text-[12.5px] text-slate-400">No job-site addresses — purchases for this job go to your company main address.</div>}
        </div>
        {addressOptions.restricted && addressOptions.addresses.length === 0 && (
          <div className="text-[12px] text-amber-700 mt-2">This job has no shipping address in QuickBooks and there's no company main address yet — add the shipping address in QuickBooks, or mark your main address as the default under Delivery Addresses.</div>
        )}
        {unattachedAddresses.length > 0 && (
          <div className="flex items-center gap-2 mt-3">
            <SearchableSelect value={attachId} onChange={(e) => setAttachId(e.target.value)} data-testid="attach-address-select" className="flex-1 h-9 px-2.5 rounded-lg border border-slate-200 text-[12.5px]">
              <option value="">Attach an existing address…</option>
              {unattachedAddresses.map((a) => <option key={a.id} value={a.id}>{a.label}{a.is_default ? " (main address)" : ""}</option>)}
            </SearchableSelect>
            <button onClick={attachAddress} data-testid="attach-address-button" className="px-3 h-9 rounded-lg border border-slate-200 text-[12.5px] font-semibold hover:bg-slate-50">Attach</button>
          </div>
        )}
      </div>

      )}
      <div className="border border-slate-200 rounded-xl p-5 mt-5">
        <h2 className="font-semibold text-[14px]">Budget <span className="text-slate-400 font-normal">(optional)</span></h2>
        <div className="mt-3">
          <label className="text-[12.5px] text-slate-500">Total budget</label>
          {budgetLines.length ? (
            <div data-testid="job-budget-total-input" className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-100 bg-slate-50 text-sm flex items-center mono">{fmt(linesTotal)}</div>
          ) : (
            <input type="number" value={budgetTotal} onChange={(e) => setBudgetTotal(e.target.value)} placeholder="Leave blank if no budget" data-testid="job-budget-total-input" className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
          )}
          {budgetLines.length > 0 && <p className="text-[11.5px] text-slate-400 mt-1">Total of the budget lines below.</p>}
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between">
            <label className="text-[12.5px] text-slate-500">Per-cost-code budget lines</label>
            <button onClick={addBudgetLine} data-testid="add-budget-line-button" className="text-[12px] font-semibold text-sky-600">+ Add line</button>
          </div>
          <div className="mt-2 space-y-2">
            {budgetLines.map((l, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`budget-line-${i}`}>
                <SearchableSelect value={l.cost_code_id} onChange={(e) => updateBudgetLine(i, { cost_code_id: e.target.value })} data-testid={`budget-line-code-${i}`} className="flex-1 h-9 px-2 rounded-lg border border-slate-200 text-[13px]">
                  {costCodes.map((cc) => <option key={cc.id} value={cc.id}>{cc.code} — {cc.name}</option>)}
                </SearchableSelect>
                <input type="number" value={l.amount} onChange={(e) => updateBudgetLine(i, { amount: e.target.value })} data-testid={`budget-line-amount-${i}`} className="w-32 h-9 px-2 rounded-lg border border-slate-200 text-[13px]" />
                <button onClick={() => removeBudgetLine(i)} data-testid={`remove-budget-line-${i}`} className="text-slate-400 hover:text-red-500"><Trash2 size={14} /></button>
              </div>
            ))}
            {budgetLines.length === 0 && <div className="text-[12.5px] text-slate-400">No per-cost-code budget set.</div>}
          </div>
        </div>
        <button onClick={saveBudget} data-testid="save-budget-button" className="mt-4 px-4 py-2 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-[13px] font-semibold">Save Budget</button>
      </div>

      <div className="border border-slate-200 rounded-xl p-5 mt-5">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-[14px]">Change Orders</h2>
          <button onClick={openNewCO} data-testid="new-change-order-button" className="flex items-center gap-1.5 text-[12.5px] font-semibold text-sky-600">
            <Plus size={13} /> New Change Order
          </button>
        </div>
        <div className="mt-3 space-y-2" data-testid="change-orders-list">
          {changeOrders.map((co) => (
            <div key={co.id} className="flex items-center justify-between border border-slate-100 rounded-lg px-3 py-2" data-testid={`change-order-row-${co.id}`}>
              <div>
                <div className="font-medium text-[13.5px]">{co.code} — {co.name}</div>
                <div className="text-[12px] text-slate-400 mt-0.5">{co.amount != null ? fmt(co.amount) : "No amount"}</div>
              </div>
              <div className="flex items-center gap-3">
                <StatusBadge status={co.status} />
                <button onClick={() => openEditCO(co)} data-testid={`edit-change-order-${co.id}`} className="text-slate-400 hover:text-sky-600"><Pencil size={14} /></button>
                <button onClick={() => removeCO(co)} data-testid={`delete-change-order-${co.id}`} className="text-slate-400 hover:text-red-500"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
          {changeOrders.length === 0 && <div className="text-slate-400 text-sm text-center py-6">No change orders yet.</div>}
        </div>
      </div>

      <Dialog open={showCO} onOpenChange={setShowCO}>
        <DialogContent data-testid="change-order-modal">
          <DialogHeader><DialogTitle>{editingCO ? "Edit Change Order" : "New Change Order"}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <input value={coForm.code} onChange={(e) => setCoForm({ ...coForm, code: e.target.value })} placeholder="Code (e.g. CO-01)" data-testid="co-code-input" className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
            <input value={coForm.name} onChange={(e) => setCoForm({ ...coForm, name: e.target.value })} placeholder="Name" data-testid="co-name-input" className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
            <input type="number" value={coForm.amount} onChange={(e) => setCoForm({ ...coForm, amount: e.target.value })} placeholder="Amount (optional)" data-testid="co-amount-input" className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
            <SearchableSelect value={coForm.status} onChange={(e) => setCoForm({ ...coForm, status: e.target.value })} data-testid="co-status-select" className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm">
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="closed">Closed</option>
            </SearchableSelect>
            <button onClick={saveCO} data-testid="save-change-order-button" className="w-full h-10 rounded-lg bg-sky-500 text-white text-[13px] font-semibold">{editingCO ? "Save Changes" : "Create Change Order"}</button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showNewAddress} onOpenChange={setShowNewAddress}>
        <DialogContent data-testid="new-job-address-modal">
          <DialogHeader><DialogTitle>Add Job-Site Address</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <input value={addressForm.label} onChange={(e) => setAddressForm({ ...addressForm, label: e.target.value })} placeholder="Label (e.g. Site Trailer)" data-testid="job-address-label-input" className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
            <input value={addressForm.address_line1} onChange={(e) => setAddressForm({ ...addressForm, address_line1: e.target.value })} placeholder="Address line 1" data-testid="job-address-line1-input" className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
            <div className="grid grid-cols-3 gap-2">
              <input value={addressForm.city} onChange={(e) => setAddressForm({ ...addressForm, city: e.target.value })} placeholder="City" data-testid="job-address-city-input" className="h-10 px-3 rounded-lg border border-slate-200 text-sm" />
              <input value={addressForm.state} onChange={(e) => setAddressForm({ ...addressForm, state: e.target.value })} placeholder="State" data-testid="job-address-state-input" className="h-10 px-3 rounded-lg border border-slate-200 text-sm" />
              <input value={addressForm.zip} onChange={(e) => setAddressForm({ ...addressForm, zip: e.target.value })} placeholder="ZIP" data-testid="job-address-zip-input" className="h-10 px-3 rounded-lg border border-slate-200 text-sm" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <input value={addressForm.site_contact_name} onChange={(e) => setAddressForm({ ...addressForm, site_contact_name: e.target.value })} placeholder="Site contact" data-testid="job-address-contact-input" className="h-10 px-3 rounded-lg border border-slate-200 text-sm" />
              <input value={addressForm.site_manager} onChange={(e) => setAddressForm({ ...addressForm, site_manager: e.target.value })} placeholder="Site manager" data-testid="job-address-manager-input" className="h-10 px-3 rounded-lg border border-slate-200 text-sm" />
            </div>
            <button onClick={createJobAddress} data-testid="save-job-address-button" className="w-full h-10 rounded-lg bg-sky-500 text-white text-[13px] font-semibold">Add Address</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
